import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function AcceptInvite() {
  const { token } = useParams();
  const { user, refresh, loading } = useAuth();
  const nav = useNavigate();
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/invites/by-token/${token}`); setInvite(data); }
      catch (e) { setErr(errMsg(e)); }
    })();
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invites/accept/${token}`);
      await refresh();
      toast.success(`Joined ${data.org_name}`);
      nav("/app", { replace: true });
    } catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };

  if (err) return (
    <div className="min-h-screen flex items-center justify-center p-6 pc-grid-bg">
      <div className="pc-card p-8 max-w-md text-center">
        <div className="text-xl font-semibold text-white mb-2" style={{fontFamily:'Outfit'}}>Invite not available</div>
        <div className="text-rose-300 text-sm mb-4" data-testid="invite-error">{err}</div>
        <Link to="/" className="text-blue-400 hover:text-blue-300 text-sm">← Back to Compass PM</Link>
      </div>
    </div>
  );

  if (!invite) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading invite…</div>;

  if (!loading && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 pc-grid-bg">
        <div className="pc-card p-8 max-w-md text-center">
          <div className="text-xl font-semibold text-white mb-2" style={{fontFamily:'Outfit'}}>Join {invite.org_name}</div>
          <p className="text-slate-400 mb-5">{invite.invited_by_name} invited <strong className="text-white">{invite.email}</strong> as {invite.org_role}.</p>
          <div className="flex gap-2 justify-center">
            <Link to={`/login?next=/accept-invite/${token}`} className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-4 py-2 rounded-lg" data-testid="invite-signin">Sign in to accept</Link>
            <Link to={`/register?next=/accept-invite/${token}`} className="border border-slate-700 text-slate-200 px-4 py-2 rounded-lg" data-testid="invite-signup">Create account</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 pc-grid-bg">
      <div className="pc-card p-8 max-w-md text-center">
        <CheckCircle2 className="mx-auto text-blue-400 mb-3" size={36}/>
        <div className="text-xl font-semibold text-white mb-2" style={{fontFamily:'Outfit'}}>Join {invite.org_name}</div>
        <p className="text-slate-400 mb-6">{invite.invited_by_name} invited you to collaborate as <strong className="capitalize text-white">{invite.org_role}</strong>.</p>
        <button onClick={accept} disabled={busy} data-testid="invite-accept-btn" className="w-full bg-gradient-to-r from-blue-500 to-blue-700 text-white py-2.5 rounded-lg font-semibold shadow-lg shadow-blue-900/30 disabled:opacity-60">
          {busy ? "Joining…" : `Accept and join as ${invite.org_role}`}
        </button>
      </div>
    </div>
  );
}
